import React from "react";
import { IndexForms } from "@hex-insights/app-modules";
import {
	ClassNameProps,
	Column,
	Conditional,
	Else,
	Grid,
	Heading,
	If,
	Modal,
	ModalProps,
	Row,
	Section,
	StyleProps,
	TruncatedText,
	ViewableObserver,
} from "@hex-insights/core";
import { FormState } from "@hex-insights/forms";
import { InternalLink } from "@hex-insights/router";
import { Icon } from "../../../../Components/Icon";
import { useHubPageInfo } from "../../../../Contexts";
import {
	ClassYear,
	ClassYearNameFragment,
	ClassYearOrderField,
	mergeFilterInputs,
	OrderDirection,
	Student,
	StudentFilterInput,
	StudentNameDescriptionAndImageFragment,
	StudentOrder,
	StudentOrderField,
	useClassYearLookupQuery,
	useStudentNamesDescriptionsAndImagesQuery,
} from "../../../../Utilities";
import {
	StudentFilterField,
	StudentFilterForm,
	StudentFilterFormState,
	StudentFilterFormUtils,
	StudentFilterFormValues,
} from "../../../Filter";
import { StudentFormat } from "../../../Format";
import { ClassYearLink, StudentLink } from "../../../Links";
import { StudentIconNameDescription } from "../General";
import { StudentIcon } from "../Icon";
import styles from "./styles.module.css";

export type ControlledStudentsListModalProps = Omit<
	StudentsListModalProps,
	"isLoading" | "students" | "hasMore" | "loadMore"
> & {
	filters?: StudentFilterInput;
	pageSize?: number;
};

export function ControlledStudentsListModal({
	filters,
	groupBy,
	pageSize = 50,
	...props
}: ControlledStudentsListModalProps) {
	let order: StudentOrder | undefined;
	if (groupBy === "classYear") {
		order = { field: StudentOrderField.GraduationYear, direction: OrderDirection.Desc };
	}

	const filterFormState = StudentFilterFormState.useFilterFormState();
	const filterInputs = IndexForms.useFilterInput(filterFormState.formValues, StudentFilterFormUtils.toFilterInputs);

	const { loading, data, fetchMore } = useStudentNamesDescriptionsAndImagesQuery({
		variables: {
			filters: mergeFilterInputs(filterInputs, filters),
			order,
			pagination: { first: pageSize },
		},
	});
	const endCursor = data?.studentConnection.pageInfo.endCursor;
	const loadMore = React.useCallback(() => {
		fetchMore({
			variables: { pagination: { first: pageSize, after: endCursor } },
			updateQuery: (previousQueryResult, { fetchMoreResult }) => ({
				...previousQueryResult,
				studentConnection: {
					...previousQueryResult.studentConnection,
					pageInfo: fetchMoreResult.studentConnection.pageInfo,
					edges: [...previousQueryResult.studentConnection.edges, ...fetchMoreResult.studentConnection.edges],
				},
			}),
		});
	}, [fetchMore, pageSize, endCursor]);
	const students = React.useMemo(() => data?.studentConnection.edges.map((e) => e.node), [data]);

	return (
		<StudentsListModal
			isLoading={loading}
			students={students}
			filterFormState={filterFormState}
			groupBy={groupBy}
			hasMore={data?.studentConnection.pageInfo.hasNextPage}
			loadMore={loadMore}
			{...props}
		/>
	);
}

export type StudentsListModalProps = Pick<ModalProps, "ifRef" | "onClose"> & {
	heading?: JSX.Element | string;
	isLoading?: boolean;
	students: (Pick<Student, "id"> & StudentNameDescriptionAndImageFragment)[] | null | undefined;
	filterFormState?: FormState<StudentFilterFormValues.FilterFormValues>;
	groupBy?: "classYear";
	hasMore?: boolean;
	loadMore?: () => void;
} & Partial<ClassNameProps & StyleProps>;

export function StudentsListModal({
	heading = "Students",
	students,
	filterFormState,
	groupBy,
	hasMore = false,
	loadMore,
	ifRef,
	onClose,
	className,
	style,
}: StudentsListModalProps) {
	const indexPage = useHubPageInfo("Student", "index");

	return (
		<Modal size="large" ifRef={ifRef} onClose={onClose} className={className} style={style}>
			<Modal.Header>
				<Row justify="spaced-start" align="center" verticalSpacing="0.5rem">
					<Heading.H2 noMargin>{heading}</Heading.H2>

					{!!indexPage && (
						<InternalLink
							to={indexPage.to}
							className="link--primary link--no-text-decoration"
							style={{ fontSize: "0.875rem", marginLeft: "1rem", fontWeight: "bold" }}
						>
							<Row justify="spaced-start" align="center" horizontalSpacing="0.25rem">
								Go to Index <Icon.ArrowRight size={12} />
							</Row>
						</InternalLink>
					)}
				</Row>
			</Modal.Header>
			<Modal.Body>
				{!!filterFormState && (
					<Row justify="spaced-start" align="center" horizontalSpacing="1rem" style={{ marginBottom: "1.5rem" }}>
						<StudentFilterForm.Search formState={filterFormState} />
						<Row justify="spaced-start" align="center" horizontalSpacing="0.25rem">
							<StudentFilterField.GraduationYearInTooltipButton formState={filterFormState} />
							<StudentFilterField.HomeRoomSectionIDInTooltipButton formState={filterFormState} />
						</Row>
					</Row>
				)}
				<Conditional>
					<If condition={groupBy === "classYear"}>
						<StudentListGroupByClassYearContent students={students} />
					</If>
					<Else>
						<StudentsListFlatContent students={students} />
					</Else>
				</Conditional>
				<If condition={hasMore && !!loadMore}>
					{!!loadMore && <ViewableObserver rootMargin="0px 0px 150px" onEnterView={loadMore} />}
				</If>
			</Modal.Body>
		</Modal>
	);
}

type StudentListGroupByClassYearContentProps = {
	students: StudentsListModalProps["students"];
};

function StudentListGroupByClassYearContent({ students }: StudentListGroupByClassYearContentProps) {
	const { data: classYearData } = useClassYearLookupQuery({
		variables: { order: { field: ClassYearOrderField.YearsFromGraduation, direction: OrderDirection.Desc } },
	});

	const studentsByGraduationYear = React.useMemo(() => {
		const studentsByGraduationYear: Record<number, Exclude<StudentsListModalProps["students"], null | undefined>> = {};

		if (!students) {
			return studentsByGraduationYear;
		}

		for (let i = 0; i < students.length; i++) {
			const student = students[i];
			const key = student.graduationYear ?? 0;
			if (!studentsByGraduationYear[key]) {
				studentsByGraduationYear[key] = [];
			}
			studentsByGraduationYear[key].push(student);
		}

		return studentsByGraduationYear;
	}, [students]);

	return (
		<Column justify="spaced-start">
			{classYearData?.classYearConnection.edges.map(({ node: classYear }) => {
				const students = studentsByGraduationYear[classYear.graduationYear] ?? [];

				if (students.length === 0) {
					return null;
				}

				return <ClassYearSection key={classYear.id} classYear={classYear} students={students} />;
			})}

			<If condition={(studentsByGraduationYear[0] ?? []).length > 0}>
				<ClassYearSection classYear={null} students={studentsByGraduationYear[0]} />
			</If>
		</Column>
	);
}

type ClassYearSectionProps = {
	classYear: (Pick<ClassYear, "id"> & ClassYearNameFragment) | null;
	students: Exclude<StudentsListModalProps["students"], null | undefined>;
};

function ClassYearSection({ classYear, students }: ClassYearSectionProps) {
	return (
		<Section>
			<Section.Header>
				<Heading.H3 noMargin>
					<Conditional>
						<If condition={!!classYear}>
							{!!classYear && <ClassYearLink instance={classYear} className="link--no-text-decoration" />}
						</If>
						<Else>Other</Else>
					</Conditional>
				</Heading.H3>
			</Section.Header>
			<Section.Body>
				<Grid tracks="auto-fill" minTrackWidth="12.5rem" gap="1rem">
					{students.map((student) => (
						<StudentLink key={student.id} instance={student} className="link--no-text-decoration">
							<Column justify="spaced-start" align="center" verticalSpacing="0.375rem">
								<StudentIcon student={student} size={52} />
								<TruncatedText className={styles["students-list-modal--group-by-class-year__student-item__name"]}>
									{StudentFormat.name(student)}
								</TruncatedText>
							</Column>
						</StudentLink>
					))}
				</Grid>
			</Section.Body>
		</Section>
	);
}

type StudentsListFlatContentProps = {
	students: StudentsListModalProps["students"];
};

function StudentsListFlatContent({ students }: StudentsListFlatContentProps) {
	return (
		<Grid tracks="auto-fill" minTrackWidth="18rem" gap="1rem">
			{students?.map((student) => (
				<StudentIconNameDescription key={student.id} student={student} />
			))}
		</Grid>
	);
}
